import React from 'react';
import FieldTypesEnum from '../../../Enums/FieldTypesEnum';
import TabListTableCellWidthEnum from '../../../Enums/TabListTableCellWidthEnum';
import FaIcon from '../../../Elements/FaIcon';
import FigmaBadge from '../../../Elements/FigmaBadge';
import LongText from '../../../Elements/LongText';
import DataTableActionMenu from '../../../Elements/DataTable/DataTableActionMenu';
import TpPlatformInvoiceStatusesEnum from '../../../Enums/TpPlatformInvoiceStatusesEnum';
import { getPlatformInvoiceStatus } from '../../../Helpers/platformInvoiceStatusHelper';
import TpPlatformInvoiceItemsTab from '../../../Contents/Operations/TpPlatformInvoice/TpPlatformInvoiceItemsTab';
import TpPlatformInvoiceCostTypesEnum from '../../../Enums/TpPlatformInvoiceCostTypesEnum';
import TpPlatformInvoiceAssignmentItem from './TpPlatformInvoiceAssignmentItem';
import TpPlatformInvoiceAdditionalFeeItem from './TpPlatformInvoiceAdditionalFeeItem';
import TpPlatformInvoiceDevelopmentItem from './TpPlatformInvoiceDevelopmentItem';
import TabbedPage from '../../../Elements/TabbedPage';
import TpPlatformInvoiceCostSummaryTab from '../../../Contents/Operations/TpPlatformInvoice/TpPlatformInvoiceCostSummaryTab';
import PlatformCostSummaryCostTypesEnum from '../../../Enums/PlatformCostSummaryCostTypesEnum';
import TpPlatformInvoiceSummaryAssignmentItem from './TpPlatformInvoiceSummaryAssignmentItem';
import TpPlatformInvoiceSummaryAdditionalItem from './TpPlatformInvoiceSummaryAdditionalItem';
import TpPlatformInvoiceSummaryDevelopmentItem from './TpPlatformInvoiceSummaryDevelopmentItem';

export default class TpPlatformInvoiceItem {
  constructor(item) {
    if (item === undefined) {
      this.TpPlatformInvoiceId = '';
      this.ThirdPartySource = '';
      this.ThirdPartyInvoiceId = '';
      this.InvoiceNumber = '';
      this.InvoiceNumberOrId = '';
      this.InvoiceName = '';
      this.InvoiceDate = null;
      this.SummarizedOnUtc = null;
      this.IgnoredOnUtc = null;
      this.BillingPeriodOverride = '';
      this.BillingPeriod = '';
      this.EffectiveBillingPeriod = '';
      this.IsBillingPeriodOverridden = false;
      this.ItemCount = '';
      this.TotalAmount = '';
      this.CurrencyCode = '';
      this.CreatedOnUtc = null;
      this.ModifiedOnUtc = null;
      this.Status = '';
      return;
    }
    this.TpPlatformInvoiceId = item.tpPlatformInvoiceId;
    this.ThirdPartySource = item.thirdPartySource;
    this.ThirdPartyInvoiceId = item.thirdPartyInvoiceId;
    this.InvoiceNumber = item.thirdPartyInvoiceNumber ? item.thirdPartyInvoiceNumber : '';
    // Prism sends an empty invoice_number on PrismIntl invoices (they plan to fix it) —
    // fall back to the platform's invoice id so the details-opener cell is never blank.
    this.InvoiceNumberOrId = this.InvoiceNumber !== '' ? this.InvoiceNumber : item.thirdPartyInvoiceId;
    this.InvoiceName = item.thirdPartyInvoiceName;
    this.InvoiceDate = item.thirdPartyInvoiceDate;
    this.SummarizedOnUtc = item.summarizedOnUtc;
    this.IgnoredOnUtc = item.ignoredOnUtc;
    this.BillingPeriodOverride = item.billingPeriodOverride ? item.billingPeriodOverride : '';
    this.BillingPeriod = item.billingPeriod ?? '';
    // One displayed period: the manual override wins over the import-computed value
    // (the override usually exists precisely because the computed one is empty/wrong).
    this.EffectiveBillingPeriod = this.BillingPeriodOverride !== '' ? this.BillingPeriodOverride : this.BillingPeriod;
    this.IsBillingPeriodOverridden = this.BillingPeriodOverride !== '';
    this.ItemCount = item.itemCount;
    // Platform-side invoice total: the amounts the platform billed, plus the agreed per-shop fee
    // for shop lines it sends count-only (Prism NA). Rendered by the MoneyFields machinery.
    this.TotalAmount = item.totalAmount ?? '';
    this.CurrencyCode = item.currencyCode ? item.currencyCode : '';
    this.CreatedOnUtc = item.createdOnUtc;
    this.ModifiedOnUtc = item.modifiedOnUtc;
    this.Status = getPlatformInvoiceStatus(item.summarizedOnUtc, item.ignoredOnUtc);
  }

  static displayedInList = 'InvoiceName';

  static NameOfTheIdField() {
    return 'TpPlatformInvoiceId';
  }

  static nameOfTheItemDetailList(item) {
    if (item) {
      return (
        <span>
          Platform Invoice -{' '}
          <LongText
            text={`${item.InvoiceName} (${item.ThirdPartyInvoiceId})`}
            maxLength={40}
          />
        </span>
      );
    }
    return 'Platform Invoice';
  }

  static MoneyFields = ['TotalAmount'];
  static Currency() {
    return 'CurrencyCode';
  }
  static MoneyFieldsSecondary = [];
  static CurrencySecondary() {
    return '';
  }

  static DateFields = ['SummarizedOnUtc', 'IgnoredOnUtc', 'CreatedOnUtc', 'ModifiedOnUtc'];
  static DateOnlyFields = ['InvoiceDate'];
  static BoolFields = [];

  static FieldsToShowInListTable = [
    {
      tabName: 'General',
      tabLabel: 'General',
      icon: 'fa-solid fa-home',
      data: [
        {
          name: 'ThirdPartyInvoiceId',
          label: 'Invoice ID',
          width: TabListTableCellWidthEnum.Single,
          className: 'text-left',
        },
        { name: 'ThirdPartySource', label: 'Source', width: TabListTableCellWidthEnum.Single, className: 'text-left' },
        {
          name: 'InvoiceNumber',
          label: 'Invoice Number',
          width: TabListTableCellWidthEnum.Single,
          className: 'text-left',
        },
        { name: 'InvoiceName', label: 'Invoice Name', width: TabListTableCellWidthEnum.Single, className: 'text-left' },
        { name: 'InvoiceDate', label: 'Invoice Date', width: TabListTableCellWidthEnum.Single, className: 'text-left' },
        {
          name: (item) => TpPlatformInvoiceItem.getStatusCellContent(item),
          label: 'Status',
          width: TabListTableCellWidthEnum.Single,
          className: 'text-left',
        },
        {
          name: 'SummarizedOnUtc',
          label: 'Summarized On',
          width: TabListTableCellWidthEnum.Single,
          className: 'text-left',
        },
        { name: 'IgnoredOnUtc', label: 'Ignored On', width: TabListTableCellWidthEnum.Single, className: 'text-left' },
        {
          name: 'BillingPeriod',
          label: 'Billing Period',
          width: TabListTableCellWidthEnum.Single,
          className: 'text-left',
        },
        {
          name: 'BillingPeriodOverride',
          label: 'Billing Period Override',
          width: TabListTableCellWidthEnum.Single,
          className: 'text-left',
        },
        { name: 'ItemCount', label: 'Item Count', width: TabListTableCellWidthEnum.Single, className: 'text-left' },
        { name: 'TotalAmount', label: 'Total', width: TabListTableCellWidthEnum.Single, className: 'text-left' },
        { name: 'CreatedOnUtc', label: 'Imported On', width: TabListTableCellWidthEnum.Single, className: 'text-left' },
        {
          name: 'ModifiedOnUtc',
          label: 'Modified On',
          width: TabListTableCellWidthEnum.Single,
          className: 'text-left',
        },
      ],
    },
    {
      tabName: 'Invoice Items',
      tabLabel: 'Invoice Items',
      icon: 'fa-solid fa-list',
      data: [
        {
          name: (item) => TpPlatformInvoiceItem.getItemsContent(item),
          label: 'Invoice Items',
          width: TabListTableCellWidthEnum.Table,
        },
      ],
    },
    {
      tabName: 'Summarized Invoice Items',
      tabLabel: 'Summarized Invoice Items',
      icon: 'fa-solid fa-calculator',
      data: [
        {
          name: (item) => TpPlatformInvoiceItem.getSummarizedItemsContent(item),
          label: 'Summarized Invoice Items',
          width: TabListTableCellWidthEnum.Table,
        },
      ],
    },
  ];

  // The two top-level item tabs both render a TabbedPage at the same popup slot, and the
  // popup (TabListTableFigma) reuses that slot across tab switches — the distinct keys force
  // a remount per tab. Inside TabbedPage the sub-tab content is already keyed per sub-tab.
  static getItemsContent(item) {
    const tabs = [
      {
        label: 'All',
        id: 'all',
        component: (
          <TpPlatformInvoiceItemsTab
            key='invoice-items-all'
            tpPlatformInvoiceId={item.TpPlatformInvoiceId}
          />
        ),
      },
      {
        label: 'Assignment',
        id: 'assignment',
        component: (
          <TpPlatformInvoiceItemsTab
            key='invoice-items-assignment'
            tpPlatformInvoiceId={item.TpPlatformInvoiceId}
            costType={TpPlatformInvoiceCostTypesEnum.ShopInstance}
            lineItemType={TpPlatformInvoiceAssignmentItem}
          />
        ),
      },
      {
        label: 'Additional',
        id: 'additional',
        component: (
          <TpPlatformInvoiceItemsTab
            key='invoice-items-additional'
            tpPlatformInvoiceId={item.TpPlatformInvoiceId}
            costType={TpPlatformInvoiceCostTypesEnum.AdditionalFee}
            lineItemType={TpPlatformInvoiceAdditionalFeeItem}
          />
        ),
      },
      {
        label: 'Development',
        id: 'development',
        component: (
          <TpPlatformInvoiceItemsTab
            key='invoice-items-development'
            tpPlatformInvoiceId={item.TpPlatformInvoiceId}
            costType={TpPlatformInvoiceCostTypesEnum.Programming}
            lineItemType={TpPlatformInvoiceDevelopmentItem}
          />
        ),
      },
    ];
    return (
      <TabbedPage
        key='invoice-items-tabs'
        tabs={tabs}
      />
    );
  }

  static getSummarizedItemsContent(item) {
    const tabs = [
      {
        label: 'All',
        id: 'all',
        component: (
          <TpPlatformInvoiceCostSummaryTab
            key='summarized-all'
            thirdPartySource={item.ThirdPartySource}
            thirdPartyInvoiceId={item.ThirdPartyInvoiceId}
          />
        ),
      },
      {
        label: 'Assignment',
        id: 'assignment',
        component: (
          <TpPlatformInvoiceCostSummaryTab
            key='summarized-assignment'
            thirdPartySource={item.ThirdPartySource}
            thirdPartyInvoiceId={item.ThirdPartyInvoiceId}
            costType={PlatformCostSummaryCostTypesEnum.Assignment}
            lineItemType={TpPlatformInvoiceSummaryAssignmentItem}
          />
        ),
      },
      {
        label: 'Additional',
        id: 'additional',
        component: (
          <TpPlatformInvoiceCostSummaryTab
            key='summarized-additional'
            thirdPartySource={item.ThirdPartySource}
            thirdPartyInvoiceId={item.ThirdPartyInvoiceId}
            costType={PlatformCostSummaryCostTypesEnum.Additional}
            lineItemType={TpPlatformInvoiceSummaryAdditionalItem}
          />
        ),
      },
      {
        label: 'Development',
        id: 'development',
        component: (
          <TpPlatformInvoiceCostSummaryTab
            key='summarized-development'
            thirdPartySource={item.ThirdPartySource}
            thirdPartyInvoiceId={item.ThirdPartyInvoiceId}
            costType={PlatformCostSummaryCostTypesEnum.Development}
            lineItemType={TpPlatformInvoiceSummaryDevelopmentItem}
          />
        ),
      },
    ];
    return (
      <TabbedPage
        key='summarized-items-tabs'
        tabs={tabs}
      />
    );
  }

  static getStatusCellContent(item) {
    // Named-variant pills are auto-width — a fixed width clipped the 'Summarized' label.
    return (
      <FigmaBadge
        variant={item.Status === TpPlatformInvoiceStatusesEnum.Summarized ? 'active' : 'inactive'}
        label={item.Status}
      />
    );
  }

  // Bound by TpPlatformInvoiceAdmin (the NewProjectItem pattern) — opens the Bravo-jobs
  // popup for the whole invoice.
  static viewBravoJobsFunction = (item) => {
    alert('Empty function');
  };

  // Bound by TpPlatformInvoiceAdmin — opens the Set Billing Period wizard (role-gated).
  static setBillingPeriodFunction = (item) => {
    alert('Empty function');
  };

  static getActionsContent(item) {
    return (
      <DataTableActionMenu
        options={[
          {
            iconName: 'fa-solid fa-list-check',
            text: 'View Bravo Jobs',
            onClick: () => TpPlatformInvoiceItem.viewBravoJobsFunction(item),
            disabled: item.Status !== TpPlatformInvoiceStatusesEnum.Summarized,
            tooltipText:
              item.Status !== TpPlatformInvoiceStatusesEnum.Summarized
                ? 'Only summarized invoices have Bravo jobs'
                : 'Show the billed Bravo jobs behind this invoice',
          },
          {
            iconName: 'fa-solid fa-calendar-pen',
            text: 'Set Billing Period',
            onClick: () => TpPlatformInvoiceItem.setBillingPeriodFunction(item),
            roleKey: 'platforminvoice_setbillingperiod',
            tooltipText: 'Set or clear the manual billing-period override',
          },
        ]}
      />
    );
  }

  static formatDate(date) {
    if (!date) return '';
    const formattedDate = new Date(date).toLocaleDateString();
    return formattedDate;
  }

  static FieldsToShowInDataTable() {
    return [
      {
        label: 'Invoice Number',
        orderById: 'ThirdPartyInvoiceNumber',
        data: 'InvoiceNumberOrId',
        minWidth: 50,
        type: FieldTypesEnum.DataFieldWithDetails,
      },
      {
        label: 'Source',
        orderById: 'ThirdPartySource',
        data: 'ThirdPartySource',
        minWidth: 50,
      },
      {
        label: 'Invoice Name',
        orderById: 'ThirdPartyInvoiceName',
        data: (d) => (
          <LongText
            text={d.InvoiceName}
            maxLength={50}
          />
        ),
        className: 'text-left',
        minWidth: 120,
      },
      {
        label: 'Invoice Date',
        orderById: 'ThirdPartyInvoiceDate',
        data: (item) => TpPlatformInvoiceItem.formatDate(item.InvoiceDate),
        minWidth: 50,
      },
      {
        label: 'Status',
        orderById: '',
        data: (item) => TpPlatformInvoiceItem.getStatusCellContent(item),
        className: 'text-center',
        minWidth: 60,
      },
      {
        label: 'Billing Period',
        orderById: 'BillingPeriod',
        data: (item) => TpPlatformInvoiceItem.getBillingPeriodCellContent(item),
        className: 'text-center',
        minWidth: 50,
      },
      {
        label: 'Total',
        orderById: '',
        data: 'TotalAmount',
        minWidth: 50,
      },
      {
        label: ' ',
        orderById: 'TpPlatformInvoiceId',
        data: TpPlatformInvoiceItem.getActionsContent,
        type: FieldTypesEnum.ActionButton,
      },
    ];
  }

  static getBillingPeriodCellContent(item) {
    if (!item.IsBillingPeriodOverridden) {
      return item.EffectiveBillingPeriod;
    }
    return (
      <span title='Billing period set manually by an administrator (override)'>
        {item.EffectiveBillingPeriod}
        <FaIcon
          iconName='fa-solid fa-user-pen'
          innerClassName='text-warning'
          tooltipText='Billing period set manually by an administrator (override)'
        />
      </span>
    );
  }
}
